/**
 * TRACI-27: order lookup that keeps the shopper's email and ZIP out of the chat.
 *
 * The order card posts them straight to the backend's /api/orders/lookup, in the
 * request body only (never a URL, header, chat message, log line or storage). The
 * backend answers with just the allowlisted order view and an opaque, session-bound
 * order ref.
 */

import { getBackendApiUrl } from '../config/backend';

export interface OrderItem {
  product: string;
  size?: string;
  quantity: number;
}

export interface OrderView {
  status: string;
  carrier?: string;
  tracking_number?: string;
  tracking_url?: string;
  order_date?: string;
  ship_date?: string;
  delivery_date?: string;
  items: OrderItem[];
}

export type OrderLookupResult =
  | { kind: 'found'; orderRef: string; order: OrderView }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

const GENERIC_ERROR = "We couldn't check that order right now. Please try again in a minute.";

/**
 * Off by default everywhere, laptops included, until order tracking is ready (TRACI-1049).
 * VITE_ORDER_LOOKUP_ENABLED=true turns it on; a prod build ignores the flag because the
 * backend has no real order provider there.
 */
export function isOrderLookupEnabled(
  env: string | undefined = import.meta.env.VITE_ENV,
  flag: string | undefined = import.meta.env.VITE_ORDER_LOOKUP_ENABLED,
): boolean {
  if ((env || '').trim().toLowerCase() === 'prod') return false;
  return (flag || '').trim().toLowerCase() === 'true';
}

export function isValidZip(zip: string): boolean {
  return /^\d{5}(-\d{4})?$/.test(zip.trim());
}

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export async function lookupOrder(
  sessionId: string,
  email: string,
  zip: string,
  getToken: () => Promise<string | undefined>,
): Promise<OrderLookupResult> {
  let resp: Response;
  try {
    const token = await getToken();
    resp = await fetch(`${getBackendApiUrl()}/api/orders/lookup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ sessionId, email: email.trim(), zip: zip.trim() }),
    });
  } catch {
    return { kind: 'error', message: GENERIC_ERROR };
  }
  if (resp.status === 429) {
    return { kind: 'error', message: 'Too many order checks for now. Please try again later.' };
  }
  if (resp.status === 422) {
    return { kind: 'error', message: 'Check the email and ZIP code, then try again.' };
  }
  if (!resp.ok) return { kind: 'error', message: GENERIC_ERROR };
  try {
    const body = await resp.json();
    if (body?.found && body.order && body.orderRef) {
      return { kind: 'found', orderRef: body.orderRef, order: body.order as OrderView };
    }
    return { kind: 'not_found' };
  } catch {
    return { kind: 'error', message: GENERIC_ERROR };
  }
}
